import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FaeKey, Blessing } from '../../../types';
import { capitalize } from '../../../utils/capitalize';
import { SWORN_DATA, FAE_COLORS, ALL_BLESSINGS_MAP, FAE_KEYS } from '../../../gameData';
import { getIconPath } from '../../../utils/iconPath';
import { Check, ChevronUp, Filter } from 'lucide-react';
import { BlessingItem, getBlessingText } from './ui/BlessingItem';
import { FaeFilterIcon } from './ui/FaeFilterIcon';

interface FaeCompendiumViewProps {
    selectedFae: FaeKey;
    selectedBlessings?: Set<string>;
    toggleBlessing?: (name: string) => void;
    clearBlessings?: () => void;
}



const getTypeColor = (typeId: string) => {
    const compendiumConfig = SWORN_DATA.ui_config?.compendium;
    const blessingColors = SWORN_DATA.blessing_type_colors || {};
    const keyMap = compendiumConfig?.blessing_type_colors_map || {};
    return blessingColors[keyMap[typeId]] || '#e6a84f';
};

const getTextColorForBg = (hex: string) => {
    const compendiumConfig = SWORN_DATA.ui_config?.compendium;
    const darkColors = compendiumConfig?.dark_text_colors || [];
    return darkColors.includes(hex) ? 'white' : 'black';
};



const TypeFilterButton = React.memo(({ 
  type, 
  isSelected, 
  onToggle,
  className = "pl-2"
}: { 
  type: { id: string; label: string }; 
  isSelected: boolean; 
  onToggle: (id: string) => void;
  className?: string;
}) => {
  const color = getTypeColor(type.id);
  return (
    <td className={className}>
      <button
        onClick={() => onToggle(type.id)}
        className={`flex-shrink-0 px-2 py-1 h-6 text-[10px] font-medium rounded-md transition-colors whitespace-nowrap border flex items-center gap-1 ${
          isSelected
            ? ''
            : 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700'
        }`}
        style={isSelected ? {
            backgroundColor: color,
            borderColor: color,
            color: getTextColorForBg(color)
        } : {}}
      >
        {type.label}
      </button>
    </td>
  );
});

const FaeGroup = React.memo(({ 
  faeKey, 
  blessings, 
  selectedBlessings, 
  toggleBlessing, 
  getBlessingTypeDisplayName 
}: { 
  faeKey: string; 
  blessings: (Blessing & { fae: FaeKey })[]; 
  selectedBlessings?: Set<string>; 
  toggleBlessing?: (name: string) => void; 
  getBlessingTypeDisplayName: (type: string) => string; 
}) => {
  const color = FAE_COLORS[faeKey as FaeKey];
  return (
    <div>
      <h4 
        className="text-xl font-bold border-b border-dashed pb-2 mb-3 flex items-center gap-2"
        style={{ color: color.hex, borderColor: color.hex }}
      >
        <img src={getIconPath('fae', faeKey)} alt={faeKey} className="rounded-full border border-slate-600 img-original-size flex-shrink-0" />
        {capitalize(faeKey)}
      </h4>
      <div className="flex flex-col gap-2">
        {blessings.map((blessing) => {
          const isSelected = selectedBlessings?.has(blessing.name) || false;
          return (
            <BlessingItem
              key={blessing.name}
              blessing={blessing}
              color={color}
              isSelected={isSelected}
              toggleBlessing={toggleBlessing}
              getBlessingTypeDisplayName={getBlessingTypeDisplayName}
            />
          );
        })}
      </div>
    </div>
  );
});

const FaeCompendiumView: React.FC<FaeCompendiumViewProps> = ({ selectedFae, selectedBlessings, toggleBlessing, clearBlessings }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [faeFilter, setFaeFilter] = useState<Set<FaeKey>>(new Set([selectedFae]));
    const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
    const [isTypeMenuOpen, setIsTypeMenuOpen] = useState(false);
    const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);

    const compendiumConfig = SWORN_DATA.ui_config?.compendium;
    const blessingTypes = compendiumConfig?.blessing_types || [];

    // When the initially selected Fae changes, update the filter
    useEffect(() => {
        setFaeFilter(new Set([selectedFae]));
    }, [selectedFae]);

    const handleFaeFilterToggle = useCallback((fae: FaeKey) => {
        setFaeFilter(prev => {
            const newSet = new Set(prev);
            if (newSet.has(fae)) {
                newSet.delete(fae);
            } else {
                newSet.add(fae);
            }
            return newSet;
        });
    }, []);

    const handleTypeFilterToggle = useCallback((typeId: string) => {
        setTypeFilter(prev => {
            const newSet = new Set(prev);
            if (newSet.has(typeId)) {
                newSet.delete(typeId);
            } else {
                newSet.add(typeId);
            }
            return newSet;
        });
    }, []);

    const filteredBlessings = useMemo(() => {
        return Array.from(ALL_BLESSINGS_MAP.values()).filter(blessing => {
            const nameMatch = blessing.name.toLowerCase().includes(searchTerm.toLowerCase());
            const effectMatch = getBlessingText(blessing).toLowerCase().includes(searchTerm.toLowerCase());
            const faeMatch = faeFilter.size === 0 || faeFilter.has(blessing.fae);
            const typeMatch = typeFilter.size === 0 || typeFilter.has(blessing.type);
            return (nameMatch || effectMatch) && faeMatch && typeMatch;
        });
    }, [searchTerm, faeFilter, typeFilter]);

    const groupedBlessings = useMemo(() => {
        const groups: Record<FaeKey, typeof filteredBlessings> = {} as Record<FaeKey, typeof filteredBlessings>;
        filteredBlessings.forEach(b => {
            if (!groups[b.fae]) groups[b.fae] = [];
            groups[b.fae].push(b);
        });

        // Sort blessings within each group
        const sortOrder = compendiumConfig?.blessing_sort_order || {};

        Object.keys(groups).forEach(key => {
            groups[key as FaeKey].sort((a, b) => {
                const orderA = sortOrder[a.type] ?? 99;
                const orderB = sortOrder[b.type] ?? 99;
                if (orderA !== orderB) return orderA - orderB;
                return a.name.localeCompare(b.name);
            });
        });

        return groups;
    }, [filteredBlessings, compendiumConfig]);

    const handleClearBlessings = useCallback(() => {
        if (window.confirm('Are you sure you want to clear all selected blessings? This action cannot be undone.')) {
            clearBlessings?.();
        }
    }, [clearBlessings]);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const scrollTop = e.currentTarget.scrollTop;
        if (!isFiltersCollapsed && scrollTop > 20) {
            setIsFiltersCollapsed(true);
        } else if (isFiltersCollapsed && scrollTop === 0) {
            setIsFiltersCollapsed(false);
        }
    }, [isFiltersCollapsed]);

    const getBlessingTypeDisplayName = (type: string): string => {
        return compendiumConfig?.blessing_type_display_names?.[type] || compendiumConfig?.blessing_type_display_names?.['default'] || 'Ability';
    };

    return (
        <div className="h-full bg-slate-900/95 backdrop-blur-sm rounded-lg border border-sworn-brown pl-4 py-4 pr-0 flex flex-col">
            {/* Header */}
            <div className="flex-shrink-0 mb-4 pb-2 border-b border-sworn-brown flex justify-between items-center pr-4">
                <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-bold text-sworn-gold">Blessing Compendium</h2>
                    <button 
                        onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
                        className="p-1 rounded-md hover:bg-slate-800 text-sworn-gold transition-colors flex items-center justify-center"
                        title={isFiltersCollapsed ? "Show Filters" : "Hide Filters"}
                    >
                        {isFiltersCollapsed ? <Filter size={20} /> : <ChevronUp size={20} />}
                    </button>
                </div>
                {clearBlessings && (
                    <button
                        onClick={handleClearBlessings}
                        className="mr-12 px-2 py-0.5 text-[10px] font-bold bg-red-900/50 text-red-200 border border-red-700 rounded hover:bg-red-800/50 transition-colors"
                    >
                        Clear Selection
                    </button>
                )}
            </div>

            {/* Filter and Search Controls */}
            {!isFiltersCollapsed && (
            <div className="flex-shrink-0 pb-4 space-y-1 pr-4">
                <div className="flex gap-2">
                    <input
                        type="text"
                        placeholder="Search blessings..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="flex-1 bg-slate-800 border border-slate-600 rounded-md px-2 py-1 text-xs text-white placeholder-slate-400 focus:ring-sworn-gold focus:border-sworn-gold"
                    />
                    <button
                        onClick={() => {
                            setSearchTerm('');
                            setFaeFilter(new Set());
                            setTypeFilter(new Set());
                        }}
                        className="px-2 py-1 bg-slate-700 text-white text-xs font-medium rounded-md hover:bg-slate-600 border border-slate-600 whitespace-nowrap"
                    >
                        Clear Filters
                    </button>
                </div>

                {/* Row 1: Fae Filters */}
                <div className="overflow-x-auto pb-1 pt-1 px-1 scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent">
                    <table>
                        <tbody>
                            <tr>
                                <td>
                                    <button 
                                        onClick={() => {
                                            if (faeFilter.size === 0 || faeFilter.size === FAE_KEYS.length) {
                                                setFaeFilter(new Set([FAE_KEYS[0]]));
                                            } else {
                                                setFaeFilter(new Set());
                                            }
                                        }}
                                        className={`flex-shrink-0 px-3 py-1 h-8 text-xs font-medium rounded-md transition-colors whitespace-nowrap border mr-1 ${
                                            faeFilter.size === 0 || faeFilter.size === FAE_KEYS.length
                                                ? 'font-semibold shadow-[0_0_10px_rgba(230,168,79,0.3)]' 
                                                : 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700'
                                        }`}
                                        style={(faeFilter.size === 0 || faeFilter.size === FAE_KEYS.length) ? {
                                            backgroundColor: '#e6a84f',
                                            borderColor: '#e6a84f',
                                            color: getTextColorForBg('#e6a84f')
                                        } : {}}
                                    >
                                        Faelord
                                    </button>
                                </td>
                                <td>
                                    <div className="h-8 w-px bg-slate-700 mx-1"></div>
                                </td>
                                {FAE_KEYS.map(fae => (
                                    <FaeFilterIcon
                                        key={fae}
                                        fae={fae}
                                        isActive={faeFilter.size === 0 || faeFilter.has(fae)}
                                        colors={FAE_COLORS[fae]}
                                        onToggle={handleFaeFilterToggle}
                                    />
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Row 2: Type Filters */}
                <div className="overflow-x-auto pb-2 px-1 scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent">
                    <table>
                        <tbody>
                            <tr>
                                <td>
                                    <button
                                        onClick={() => setIsTypeMenuOpen(!isTypeMenuOpen)}
                                        className={`flex-shrink-0 px-2 py-1 h-6 text-[10px] font-medium rounded-md transition-colors whitespace-nowrap border flex items-center gap-1 ${
                                            (typeFilter.size === 0 || typeFilter.size === blessingTypes.length)
                                                ? 'font-semibold shadow-[0_0_10px_rgba(230,168,79,0.3)]'
                                                : 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700'
                                        }`}
                                        style={((typeFilter.size === 0 || typeFilter.size === blessingTypes.length)) ? {
                                            backgroundColor: '#e6a84f',
                                            borderColor: '#e6a84f',
                                            color: getTextColorForBg('#e6a84f')
                                        } : {}}
                                    >
                                        Type {typeFilter.size > 0 && `(${typeFilter.size})`}
                                        <span className={`transition-transform inline-block ml-1 font-bold ${isTypeMenuOpen ? 'rotate-90' : ''}`}>&gt;</span>
                                    </button>
                                </td>
                                {isTypeMenuOpen && blessingTypes.slice(0, 5).map(type => (
                                    <TypeFilterButton
                                        key={type.id}
                                        type={type}
                                        isSelected={typeFilter.has(type.id)}
                                        onToggle={handleTypeFilterToggle}
                                    />
                                ))}
                            </tr>
                            {isTypeMenuOpen && (
                                <tr>
                                    <td></td>
                                    {blessingTypes.slice(5).map(type => (
                                        <TypeFilterButton
                                            key={type.id}
                                            type={type}
                                            isSelected={typeFilter.has(type.id)}
                                            onToggle={handleTypeFilterToggle}
                                            className="pl-2 pt-2"
                                        />
                                    ))}
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            )}

            {/* Blessing List */}
            <div 
                className="flex-grow overflow-y-auto pr-2 space-y-6 custom-scrollbar"
                onScroll={handleScroll}
            >
                {Object.entries(groupedBlessings).map(([faeKey, blessings]) => (
                    <FaeGroup
                        key={faeKey}
                        faeKey={faeKey}
                        blessings={blessings}
                        selectedBlessings={selectedBlessings}
                        toggleBlessing={toggleBlessing}
                        getBlessingTypeDisplayName={getBlessingTypeDisplayName}
                    />
                ))}
                {filteredBlessings.length === 0 && (
                    <div className="col-span-full text-center text-slate-400 py-8">
                        No blessings match your criteria.
                    </div>
                )}
            </div>
        </div>
    );
};

export default FaeCompendiumView;
